import { DateAxis, Legend, LineSeries, ValueAxis, XYChart, XYCursor } from '@amcharts/amcharts4/charts';
import { BindingEngine } from 'aurelia-binding';
import { CustomerTotalValuesReportViewModel } from '@dynamik/common/clients/reporting-clients';
import { CustomerService, IApplicationFilter, PortfolioReportCache, WidgetPropertyType, getChartReadyPromise, property, when, widget } from '@dynamik/common';
import { color, create, percent, unuseTheme, useTheme } from '@amcharts/amcharts4/core';
import { addYears, formatISODate, parseISO } from '../../../date-fns-local';
import { ChartWidgetBase } from '@dynamik/common';
import { customElement } from 'aurelia-templating';

export enum NavChangeWidgetStartFrom {
  FromDate = 2,
  ToDatePrevYear = 4
}

@customElement('nav-change-widget')
@widget('NAV change', true, true, 'NAV change description')
export class NavChangeWidget extends ChartWidgetBase {
  constructor(element: Element, customerSettings: CustomerService, bindingEngine: BindingEngine, private portfolioReportCache: PortfolioReportCache) {
    super(element, bindingEngine, customerSettings);
  }

  chartObject: XYChart;
  totals: CustomerTotalValuesReportViewModel[];
  noGL: boolean;

  @property('Data', undefined, WidgetPropertyType.List, [
    { value: NavChangeWidgetStartFrom.ToDatePrevYear, label: '"To" date previous year' },
    { value: NavChangeWidgetStartFrom.FromDate, label: '"From" date' }
  ])
  startFrom: NavChangeWidgetStartFrom = NavChangeWidgetStartFrom.FromDate;

  @property('Data', 'NAV')
  nav: boolean = true;

  @property('Data', 'OSEBX')
  osebx: boolean = true;

  @property('Data', 'Portefølje')
  portfolio: boolean = true;

  @property('Data')
  legend: boolean = true;

  @property('Data', 'Hist. months')
  historicalMonths?: number = 0;


  @property('Style')
  chartTheme?: string;

  @property('Style')
  labelColour?: string;

  override async loadData(filter: IApplicationFilter) {
    let fromDate: Date | undefined;
    const from = filter.dates.from;
    const to = filter.dates.to;
    switch (this.startFrom) {
      case NavChangeWidgetStartFrom.FromDate:
        fromDate = from;
        break;
      case NavChangeWidgetStartFrom.ToDatePrevYear:
        fromDate = addYears(to, -1);
        break;
    }
    const fromDateStr = formatISODate(fromDate);
    let data = await this.portfolioReportCache.customerTotals(filter.customer.companyId, fromDateStr, filter.dates.toStr, undefined, false, this.historicalMonths);
    switch (this.startFrom) {
      case NavChangeWidgetStartFrom.FromDate:
        data = data.filter(x => from <= parseISO(x.dateClosingBalance) && parseISO(x.dateClosingBalance) <= to);
        break;
      case NavChangeWidgetStartFrom.ToDatePrevYear:
        break;
    }
    return data;
  }

  override async setData(data: CustomerTotalValuesReportViewModel[]) {
    this.totals = data;
    this.noGL = !data[data.length - 1]?.netAssetValueClosingBalance;
    this.excelData = {
      fileName: 'NAV Change',
      data: { Items: data }
    };
    await this.createChart();
  }

  async createChart() {
    this.totals.forEach(x => {
      x.netAssetValueChangeYearToDateInPercent *= 100;
      x.netAssetValueChangePeriodInPercent *= 100;
      x.indexSellPriceChangePeriodInPercent *= 100;
      x.indexSellpriceChangeYearToDateInPercent *= 100;
      x.profitLossChangeYearToDateInPercent *= 100;
      x.profitLossChangePeriodInPercent *= 100;
    });
    const theme = this.customerService.getChartTheme(this.chartTheme);
    useTheme(theme);
    this.chartObject?.dispose();
    this.chartObject = create(this.chart, XYChart);
    const readyPromise = getChartReadyPromise(this.chartObject);
    this.chartObject.height = percent(100);
    this.chartObject.hiddenState.properties.opacity = 0;
    this.chartObject.fontSize = 9;
    const labelColour = color(this.getChartLabelColour(this.labelColour));
    if (this.legend) {
      this.chartObject.legend = new Legend();
      this.chartObject.legend.labels.template.stroke = color(labelColour);
    }

    const xAxis = this.chartObject.xAxes.push(new DateAxis());
    xAxis.dataFields.date = 'date';
    xAxis.renderer.labels.template.wrap = true;
    xAxis.renderer.labels.template.rotation = -90;
    xAxis.renderer.minGridDistance = 30;
    xAxis.renderer.grid.template.location = 0.5;
    xAxis.renderer.labels.template.horizontalCenter = 'right';
    xAxis.renderer.labels.template.verticalCenter = 'middle';
    xAxis.renderer.labels.template.fill = color(labelColour);
    xAxis.renderer.ticks.template.stroke = color(labelColour);

    const yAxis = this.chartObject.yAxes.push(new ValueAxis());
    yAxis.numberFormatter.numberFormat = '#a';
    yAxis.tooltip.align = 'right';
    yAxis.tooltip.contentAlign = 'right';
    yAxis.updateTooltip = () => { };
    yAxis.tooltip.pointerOrientation = 'left';
    yAxis.renderer.labels.template.fill = color(labelColour);
    yAxis.renderer.ticks.template.stroke = color(labelColour);
    [
      ...when(this.nav && this.startFrom == NavChangeWidgetStartFrom.FromDate, { label: 'NAV', field: 'netAssetValueChangeYearToDateInPercent' }),
      ...when(this.nav && this.startFrom == NavChangeWidgetStartFrom.ToDatePrevYear, { label: 'NAV', field: 'netAssetValueChangePeriodInPercent' }),
      ...when(this.osebx && this.startFrom == NavChangeWidgetStartFrom.FromDate, { label: 'OSEBX', field: 'indexSellpriceChangeYearToDateInPercent' }),
      ...when(this.osebx && this.startFrom == NavChangeWidgetStartFrom.ToDatePrevYear, { label: 'OSEBX', field: 'indexSellPriceChangePeriodInPercent' }),
      ...when(this.portfolio && this.startFrom == NavChangeWidgetStartFrom.FromDate, { label: 'Portefølje', field: 'profitLossChangeYearToDateInPercent' }),
      ...when(this.portfolio && this.startFrom == NavChangeWidgetStartFrom.ToDatePrevYear, { label: 'Portefølje', field: 'profitLossChangePeriodInPercent' })
    ].forEach(x => {
      const series = this.chartObject.series.push(new LineSeries());
      series.name = x.label;
      series.stacked = false;
      series.dataFields.valueY = x.field;
      series.dataFields.dateX = 'dateClosingBalance';
      series.data = this.totals;
      series.fillOpacity = 0.5;
      series.tooltipText = '[bold]{name}[/] {dateX}: {valueY}';
      series.strokeWidth = 2;
      series.strokeOpacity = 1;
      series.fillOpacity = 0;
      series.numberFormatter.numberFormat = '#,###.##';
    });

    this.chartObject.cursor = new XYCursor();
    unuseTheme(theme);
    await readyPromise;
  }

  override configChanged(key: keyof NavChangeWidget) {
    switch (key) {
      case 'labelColour':
        const labelColour = color(this.getChartLabelColour(this.labelColour));
        this.chartObject.xAxes.each(x => x.renderer.labels.template.fill = x.renderer.ticks.template.stroke = color(labelColour));
        this.chartObject.yAxes.each(x => x.renderer.labels.template.fill = x.renderer.ticks.template.stroke = color(labelColour));
        this.chartObject.legend.labels.template.stroke = color(labelColour);
        this.refresh$.next();
        break;
      default:
        this.refresh$.next();
        break;
    }
  }
}
